<script setup lang="ts">
import intlLoadingData from '~/assets/lottie/intl-loading-new.json'

const DATE_ICON = 'https://resources1.interface003.com/web30/2026/07/03/162e2b99-4298-47b0-8c8e-197d04214746.png'
type FeedbackCategory = {
  key: string
  label: string
  count?: number
  value?: string | number
}

type FeedbackImage = {
  src: string
  alt?: string
}

type FeedbackEntry = {
  id: string
  name: string
  avatar: string
  submittedAt: string
  tag: string
  content: string
  images: FeedbackImage[]
  categoryKey?: string
  categoryLabel?: string
  detailUrl?: string
  highlighted?: boolean
}

const MAX_TABS_WIDTH = 895
const MAX_COLLAPSED_CONTROLS = 7
const MIN_VISIBLE_CATEGORY_COUNT = 3
const TAB_GAP = 8

const feedbackCategoryAliases: Record<string, string> = {
  all: 'all',
  全部: 'all',
  恶意爆仓: 'forced-liquidation',
  操纵行情: 'market-manipulation',
  恶意砸盘: 'dumping',
  内幕交易: 'insider-trading',
  野鸡交易所: 'unlicensed-exchange',
  资金盘: 'ponzi',
  空气币: 'air-coin',
  rugpull: 'rugpull',
  无法提现: 'unable-withdrawal',
  暂停提币: 'withdrawal-suspended',
  系统异常: 'system-exception',
  交易执行异常: 'trade-execution-exception',
  拒绝付款: 'payment-refusal',
  钓鱼单: 'phishing-order',
  黑钱: 'black-money',
  冻结账户: 'frozen-account',
  客服失联: 'customer-service-lost',
}

feedbackCategoryAliases['无法出金'] = 'unable-withdrawal'

const props = defineProps<{
  title?: string
  total?: number
  notice?: string
  categories?: FeedbackCategory[]
  feedbackItems?: FeedbackEntry[]
  loading?: boolean
  autoLoad?: boolean
  showLoadMore?: boolean
}>()

const emit = defineEmits<{
  select: [item: FeedbackEntry]
  categorySelect: [category: FeedbackCategory]
  loadMore: [manual?: boolean]
}>()

const { locale, localePath, t } = useAppI18n()
const nuxtLinkComponent = resolveComponent('NuxtLink')
const resolvedTitle = computed(() => props.title || t('83846'))
const resolvedNotice = computed(() => props.notice || t('84167'))
const resolvedFeedbackItems = computed(() => (Array.isArray(props.feedbackItems) ? props.feedbackItems : []))
const hasSourceCategories = computed(() => Boolean(props.categories?.length))
const formatFeedbackCount = (value: number) => (value > 999 ? '999+' : String(value))

const normalizeCategoryKey = (value?: string) => {
  const rawValue = value?.trim()

  if (!rawValue) {
    return ''
  }

  return feedbackCategoryAliases[rawValue] || feedbackCategoryAliases[rawValue.toLowerCase()] || rawValue
}

const getFeedbackItemCategoryKey = (item: FeedbackEntry) =>
  normalizeCategoryKey(item.categoryKey) || normalizeCategoryKey(item.categoryLabel) || normalizeCategoryKey(item.tag)

const sourceCategoryByKey = computed(() => {
  const categoryByKey = new Map<string, FeedbackCategory>()

  props.categories?.forEach((category) => {
    const categoryKey = normalizeCategoryKey(category.key) || normalizeCategoryKey(category.label)

    if (!categoryKey) {
      return
    }

    categoryByKey.set(categoryKey, {
      ...category,
      key: categoryKey,
    })
  })

  return categoryByKey
})

const sourceCategoryTotal = computed(() =>
  Array.from(sourceCategoryByKey.value.values()).reduce(
    (total, category) => (category.key === 'all' ? total : total + (category.count ?? 0)),
    0,
  ),
)

const resolvedTotal = computed(() => {
  const allCategory = sourceCategoryByKey.value.get('all')
  const candidates = [props.total, allCategory?.count, sourceCategoryTotal.value, resolvedFeedbackItems.value.length]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0)

  return Math.max(...candidates, 0)
})
const resolvedTotalText = computed(() => formatFeedbackCount(resolvedTotal.value))

const resolvedCategories = computed(() => {
  if (!hasSourceCategories.value) {
    return []
  }

  const categories = Array.from(sourceCategoryByKey.value.values()).map((category) => ({
    key: category.key,
    label: category.label,
    count: category.key === 'all' ? resolvedTotal.value : category.count,
    value: category.value,
  }))

  if (categories.some((category) => category.key === 'all')) {
    return categories
  }

  return [
    {
      key: 'all',
      label: t('83794'),
      count: resolvedTotal.value,
      value: 0,
    },
    ...categories,
  ]
})

const displayCategories = computed(() => resolvedCategories.value.filter((category) => (category.count ?? 0) > 0))
const hasVisibleCategoryTabs = computed(() => displayCategories.value.length >= MIN_VISIBLE_CATEGORY_COUNT)
const activeCategoryKey = ref('all')
const tabsRef = ref<HTMLElement | null>(null)
const tabsMeasureRef = ref<HTMLElement | null>(null)
const loadTriggerRef = ref<HTMLElement | null>(null)
const collapsedCategoryCount = ref(Math.min(displayCategories.value.length, MAX_COLLAPSED_CONTROLS))
const isCategoriesExpanded = ref(false)
let tabsResizeObserver: ResizeObserver | null = null
let loadTriggerObserver: IntersectionObserver | null = null

const hasHiddenCategories = computed(() => displayCategories.value.length > collapsedCategoryCount.value)
const visibleCategories = computed(() =>
  isCategoriesExpanded.value ? displayCategories.value : displayCategories.value.slice(0, collapsedCategoryCount.value),
)
const selectedCategoryKey = computed(() => (hasVisibleCategoryTabs.value ? activeCategoryKey.value : 'all'))
const filteredFeedbackItems = computed(() => {
  if (selectedCategoryKey.value === 'all' || hasSourceCategories.value) {
    return resolvedFeedbackItems.value
  }
  return resolvedFeedbackItems.value.filter((item) => getFeedbackItemCategoryKey(item) === selectedCategoryKey.value)
})
const displayFeedbackItems = computed(() => filteredFeedbackItems.value)

const canShowLoadMore = computed(() => Boolean(props.showLoadMore))
const isChineseLanguage = computed(() => locale.toLowerCase().startsWith('zh'))
const loadMoreText = computed(() => (isChineseLanguage.value ? '加载更多' : 'Load more'))
const loadingText = computed(() => (isChineseLanguage.value ? '加载中...' : 'Loading...'))

watch(displayCategories, (categories, previousCategories) => {
  const nextCategoryKeys = categories.map((category) => category.key).join('|')
  const previousCategoryKeys = previousCategories.map((category) => category.key).join('|')

  if (nextCategoryKeys !== previousCategoryKeys) {
    isCategoriesExpanded.value = false
  }

  if (!categories.some((category) => category.key === activeCategoryKey.value)) {
    activeCategoryKey.value = categories.some((category) => category.key === 'all') ? 'all' : categories[0]?.key || ''
  }

  nextTick(calculateCollapsedCategoryCount)
})

const formatCategory = (category: FeedbackCategory) =>
  typeof category.count === 'number' ? `${category.label}（${formatFeedbackCount(category.count)}）` : category.label

function calculateCollapsedCategoryCount() {
  const tabsElement = tabsRef.value
  const measureElement = tabsMeasureRef.value
  const categories = displayCategories.value

  if (!tabsElement || !measureElement || categories.length === 0) {
    collapsedCategoryCount.value = 0
    return
  }

  const availableWidth = Math.min(tabsElement.clientWidth, MAX_TABS_WIDTH)
  const categoryElements = Array.from(measureElement.querySelectorAll<HTMLElement>('[data-feedback-category-measure]'))
  const toggleElement = measureElement.querySelector<HTMLElement>('[data-feedback-toggle-measure]')
  const maxCategoryCount = Math.min(categories.length, MAX_COLLAPSED_CONTROLS)
  const visibleCategoryElements = categoryElements.slice(0, maxCategoryCount)
  const allCategoriesWidth =
    visibleCategoryElements.reduce((total, element) => total + element.offsetWidth, 0) +
    Math.max(visibleCategoryElements.length - 1, 0) * TAB_GAP

  if (categories.length <= MAX_COLLAPSED_CONTROLS && allCategoriesWidth <= availableWidth) {
    collapsedCategoryCount.value = categories.length
    return
  }

  const toggleWidth = toggleElement?.offsetWidth || 70
  const maxCategoriesBeforeToggle = Math.max(MAX_COLLAPSED_CONTROLS - 1, 1)
  let usedWidth = toggleWidth
  let categoryCount = 0

  for (const element of categoryElements.slice(0, maxCategoriesBeforeToggle)) {
    const nextWidth = usedWidth + TAB_GAP + element.offsetWidth

    if (nextWidth > availableWidth && categoryCount > 0) {
      break
    }

    usedWidth = nextWidth
    categoryCount += 1
  }

  collapsedCategoryCount.value = Math.max(categoryCount, 1)
}

const toggleCategories = () => {
  isCategoriesExpanded.value = !isCategoriesExpanded.value
}

const selectCategory = (category: FeedbackCategory) => {
  if (category.key === activeCategoryKey.value) {
    return
  }

  activeCategoryKey.value = category.key
  emit('categorySelect', category)
}

const continueAutoLoad = () => {
  emit('loadMore', true)
}

const disconnectLoadTriggerObserver = () => {
  loadTriggerObserver?.disconnect()
  loadTriggerObserver = null
}

const syncLoadTriggerObserver = () => {
  disconnectLoadTriggerObserver()

  if (!props.autoLoad || props.loading || !loadTriggerRef.value) {
    return
  }

  loadTriggerObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting) && props.autoLoad && !props.loading) {
        emit('loadMore', false)
      }
    },
    {
      rootMargin: '240px 0px',
    },
  )
  loadTriggerObserver.observe(loadTriggerRef.value)
}

// 无真实详情地址的反馈仅展示为静态卡片，避免回退 ID 生成不存在的详情链接。
const getFeedbackDetailPath = (item: FeedbackEntry) => item.detailUrl || ''

// 外部详情地址显式交给 NuxtLink 的 external 模式处理。
const isExternalFeedbackDetail = (item: FeedbackEntry) => /^https?:\/\//.test(getFeedbackDetailPath(item))

// 仅给真实详情链接注入跳转属性，静态卡片不携带链接语义。
const getFeedbackLinkProps = (item: FeedbackEntry) => {
  const detailUrl = getFeedbackDetailPath(item)

  if (!detailUrl) {
    return {}
  }

  return {
    to: detailUrl,
    external: isExternalFeedbackDetail(item),
    target: '_blank',
    rel: 'noopener noreferrer',
  }
}

// 链接跳转由 NuxtLink 负责，这里只保留原有选择事件。
const selectFeedback = (item: FeedbackEntry) => {
  emit('select', item)
}

onMounted(() => {
  calculateCollapsedCategoryCount()
  syncLoadTriggerObserver()

  tabsResizeObserver = new ResizeObserver(calculateCollapsedCategoryCount)

  if (tabsRef.value) {
    tabsResizeObserver.observe(tabsRef.value)
  }

  document.fonts?.ready.then(calculateCollapsedCategoryCount)
})

onBeforeUnmount(() => {
  tabsResizeObserver?.disconnect()
  tabsResizeObserver = null
  disconnectLoadTriggerObserver()
})

watch(() => [props.autoLoad, props.loading, displayFeedbackItems.value.length] as const, syncLoadTriggerObserver, {
  flush: 'post',
})

// 截取图片列表前 limit 个元素
const sliceImgList = (list: FeedbackImage[], limit: number) => list.slice(0, limit)
</script>

<template>
  <section
    class="feedback-details w-full rounded-[16px] bg-white p-[24px] text-[#292a2e]"
    aria-labelledby="feedback-details-title"
    data-node-id="8223:74877"
  >
    <div class="flex w-full flex-col gap-[16px]">
      <header class="flex w-full flex-col gap-[16px]">
        <div class="flex w-full items-end gap-[12px]">
          <h2
            id="feedback-details-title"
            class="shrink-0 text-[24px] leading-[32px] font-[600] whitespace-nowrap text-[#292a2e]"
            data-node-id="8223:74881"
          >
            {{ resolvedTitle }} {{ resolvedTotalText }}
          </h2>
          <div class="flex min-w-0 flex-1 items-center gap-[4px] pb-[5px]" data-node-id="8223:74882">
            <span class="feedback-details-notice-icon shrink-0" aria-hidden="true">i</span>
            <p class="min-w-0 flex-1 text-[12px] leading-[18px] font-[400] text-[#8c8f97]" data-node-id="8223:74884">
              {{ resolvedNotice }}
            </p>
          </div>
        </div>
        <div class="h-px w-full bg-[#e8e9ed]" aria-hidden="true"></div>
      </header>

      <nav
        v-if="hasVisibleCategoryTabs"
        ref="tabsRef"
        class="feedback-details-tabs flex w-full items-center gap-[8px]"
        :class="{ 'feedback-details-tabs-expanded': isCategoriesExpanded }"
        :aria-label="t('82430')"
        data-node-id="8223:74887"
      >
        <button
          v-for="category in visibleCategories"
          :key="category.key"
          type="button"
          class="feedback-details-category-button h-[36px] shrink-0 cursor-pointer rounded-[2px] border-[0.5px] px-[12px] py-[8px] text-[13px] leading-[20px] whitespace-nowrap transition-colors"
          :data-label="formatCategory(category)"
          :class="
            category.key === activeCategoryKey
              ? 'border-[#112d73] bg-[#112d73] font-[500] text-white'
              : 'border-[rgba(11,18,40,0.14)] bg-white font-[400] text-[#292a2e] hover:bg-[#f8f8f8]'
          "
          :aria-pressed="category.key === activeCategoryKey"
          @click="selectCategory(category)"
        >
          {{ formatCategory(category) }}
        </button>
        <button
          v-if="hasHiddenCategories"
          type="button"
          class="feedback-details-more flex h-[36px] min-w-[70px] shrink-0 cursor-pointer items-center justify-center gap-[4px] rounded-[2px] border-[0.5px] border-[rgba(11,18,40,0.14)] bg-white px-[12px] py-[8px] text-[13px] leading-[20px] font-[400] whitespace-nowrap text-[#292a2e] hover:bg-[#f8f8f8]"
          :aria-label="isCategoriesExpanded ? t('83802') : t('82485')"
          :aria-expanded="isCategoriesExpanded"
          @click="toggleCategories"
        >
          <span>{{ isCategoriesExpanded ? t('83802') : t('82485') }}</span>
          <span
            class="feedback-details-chevron"
            :class="{ 'feedback-details-chevron-expanded': isCategoriesExpanded }"
            aria-hidden="true"
          ></span>
        </button>
      </nav>

      <div v-if="hasVisibleCategoryTabs" ref="tabsMeasureRef" class="feedback-details-tabs-measure" aria-hidden="true">
        <span
          v-for="category in displayCategories"
          :key="`measure-${category.key}`"
          class="feedback-details-category-measure"
          data-feedback-category-measure
        >
          {{ formatCategory(category) }}
        </span>
        <span class="feedback-details-toggle-measure" data-feedback-toggle-measure>{{ t('82485') }}</span>
      </div>
    </div>

    <div class="mt-[16px] flex w-full flex-col bg-white" data-node-id="8223:74898">
      <component
        :is="getFeedbackDetailPath(item) ? nuxtLinkComponent : 'article'"
        v-for="item in displayFeedbackItems"
        :key="item.id"
        class="feedback-details-item flex w-full gap-[12px] rounded-[8px] p-[12px] transition-colors"
        :class="{ 'cursor-pointer': getFeedbackDetailPath(item), 'bg-[#f8f8f8]': item.highlighted }"
        v-bind="getFeedbackLinkProps(item)"
        @click="selectFeedback(item)"
      >
        <CommonImage
          class="h-[40px] w-[40px] shrink-0 rounded-full object-cover"
          :src="item.avatar"
          :alt="item.name"
          width="40"
          height="40"
        />

        <div class="flex min-w-0 flex-1 flex-col gap-[16px]">
          <div class="flex w-full flex-col gap-[12px]">
            <div class="flex w-full flex-col">
              <h3
                class="max-w-full truncate text-[16px] leading-[27px] font-[600] tracking-[0] text-[#111827]"
                data-node-id="8223:74904"
              >
                {{ item.name }}
              </h3>
              <div class="flex h-[20px] w-full items-center gap-[4px]">
                <CommonImage :src="DATE_ICON" alt="date icon" class="flex size-[14px] shrink-0" />
                <time class="shrink-0 text-[12px] leading-[18px] font-[400] text-[#6b7280]">
                  {{ item.submittedAt }}
                </time>
                <span
                  class="feedback-details-tag rounded-[2px] px-[8px] py-[2px] text-[11px] leading-[16px] font-[400] whitespace-nowrap text-[#505258]"
                  :class="item.highlighted ? 'bg-white' : 'bg-[#f8f8f8]'"
                >
                  {{ item.tag }}
                </span>
              </div>
            </div>

            <p class="feedback-details-content text-[16px] leading-[24px] font-[400] break-words text-[#292a2e]">
              {{ item.content }}
            </p>
          </div>

          <div v-if="item.images.length > 0" class="tst feedback-details-images flex w-full items-center gap-[12px]">
            <div
              v-for="(image, index) in sliceImgList(item.images, 6)"
              :key="`${item.id}-${image.src}-${index}`"
              class="relative"
            >
              <CommonImage
                class="h-[126px] w-[126px] shrink-0 rounded-[8px] border-[0.5px] border-[rgba(5,21,36,0.06)] object-cover"
                :src="image.src"
                :alt="image.alt"
                width="108"
                height="108"
                loading="lazy"
              />
              <div
                v-if="index === sliceImgList(item.images, 6).length - 1 && item.images?.length > 6"
                dir="ltr"
                class="absolute right-[5px] bottom-[4px] flex h-[22px] min-w-[38px] items-center justify-center rounded-[2px] bg-[rgba(5,12,31,0.46)] p-[2px_12px] font-[WikiFX] text-[12px] font-[700] text-[#fff] rtl:right-auto rtl:left-[5px]"
              >
                +{{ item.images.length - 6 }}
              </div>
            </div>
          </div>
        </div>
      </component>

      <div
        ref="loadTriggerRef"
        class="feedback-details-load-trigger flex min-h-[52px] w-full items-center justify-center py-[16px]"
      >
        <CommonBitLoadingLottie
          v-if="loading"
          class="feedback-details-loading"
          :animation-data="intlLoadingData"
          width="96px"
          height="30px"
        />
        <span v-if="loading" class="sr-only">{{ loadingText }}</span>
        <button
          v-else-if="canShowLoadMore"
          type="button"
          class="feedback-details-load-more h-[36px] min-w-[96px] rounded-[18px] border border-[rgba(11,18,40,0.14)] bg-white px-[20px] text-[13px] leading-[20px] font-[500] text-[#292a2e] hover:bg-[#f8f8f8]"
          @click="continueAutoLoad"
        >
          {{ loadMoreText }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.feedback-details {
  position: relative;
  border-radius: 12px;
  border: 1px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
}

.feedback-details-notice-icon {
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #8c8f97;
  border-radius: 50%;
  color: #8c8f97;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.feedback-details-chevron {
  width: 7px;
  height: 7px;
  margin-top: -3px;
  border-right: 1.5px solid currentcolor;
  border-bottom: 1.5px solid currentcolor;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.feedback-details-chevron-expanded {
  margin-top: 3px;
  transform: rotate(225deg);
}

.feedback-details-images {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.feedback-details-tabs {
  max-width: 895px;
  overflow: hidden;
  flex-wrap: nowrap;
}

.feedback-details-tabs-expanded {
  flex-wrap: wrap;
  overflow: visible;
}

.feedback-details-category-button::before {
  display: block;
  height: 0;
  overflow: hidden;
  content: attr(data-label);
  font-weight: 500;
  visibility: hidden;
}

.feedback-details-tabs-measure {
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  visibility: hidden;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  white-space: nowrap;
}

.feedback-details-category-measure,
.feedback-details-toggle-measure {
  box-sizing: border-box;
  display: inline-flex;
  height: 36px;
  align-items: center;
  border: 0.5px solid transparent;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
}

.feedback-details-toggle-measure {
  min-width: 70px;
  justify-content: center;
}

.feedback-details-item {
  color: inherit;
  text-decoration: none;
}

.feedback-details-item:hover,
.feedback-details-item:focus-visible {
  background: #f8f8f8;
  outline: none;
}

.feedback-details-item:hover .feedback-details-tag,
.feedback-details-item:focus-visible .feedback-details-tag {
  background: #fff;
}

.feedback-details-content {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.feedback-details-images {
  overflow-x: auto;
  overflow-y: hidden;
}

@media screen and (max-width: 720px) {
  .feedback-details {
    padding: 18px;
    border-radius: 12px;
  }

  .feedback-details header > div:first-child {
    align-items: flex-start;
    flex-direction: column;
  }

  .feedback-details-item {
    padding: 10px;
  }
}
</style>
